import { Alert } from 'antd';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap');

  :root {
    --bg:       #080808;
    --bg2:      #0f0f0f;
    --bg3:      #161616;
    --border-w: rgba(255,255,255,0.07);
    --border-w2:rgba(255,255,255,0.13);
    --gold:     #dfac06;
    --gold-glow:rgba(223,172,6,0.28);
    --gold-dim: rgba(223,172,6,0.08);
    --cyan:     #00d4d4;
    --cyan-glow:rgba(0,212,212,0.28);
    --ink-w:    rgba(255,255,255,0.85);
    --muted:    rgba(255,255,255,0.45);
    --dim:      rgba(255,255,255,0.25);
    --r:        8px;
    --r2:       12px;
  }

  .wd-root {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    color: var(--ink-w);
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
    font-size: 15px;
  }

  /* SCAN LINE OVERLAY */
  .wd-root::before {
    content: '';
    position: fixed; inset: 0; z-index: 1; pointer-events: none;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.03) 2px,
      rgba(0,0,0,0.03) 4px
    );
  }

  /* GRID BG */
  .wd-grid-bg {
    position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 32px 32px;
  }

  /* RAYS */
  .wd-rays {
    position: fixed !important;
    inset: 0; z-index: 0; pointer-events: none;
  }

  /* NAV */
  .wd-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 200;
    height: 56px; display: flex; align-items: center; justify-content: space-between;
    padding: 0 32px;
    background: rgba(8,8,8,0.88);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border-w);
  }
  .wd-nav-logo {
    font-family: 'Geist Mono', monospace; font-weight: 500; font-size: 0.95rem;
    color: #fff; text-decoration: none; display: flex; align-items: center; gap: 8px;
  }
  .wd-nav-logo img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; border: 1px solid var(--border-w2); }
  .wd-nav-right { display: flex; align-items: center; gap: 16px; }
  .wd-nav-badge {
    font-family: 'Geist Mono', monospace; font-size: 0.65rem; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase;
    padding: 3px 10px; border-radius: 999px;
    border: 1px solid rgba(223,172,6,0.4);
    color: var(--gold); background: var(--gold-dim);
  }
  .wd-nav-back {
    font-size: 0.8rem; color: var(--muted); text-decoration: none;
    font-weight: 500; transition: color 0.15s; display: flex; align-items: center; gap: 5px;
  }
  .wd-nav-back:hover { color: #fff; }

  /* HERO */
  .wd-hero {
    position: relative; z-index: 2;
    min-height: 100vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 100px 24px 80px; text-align: center;
  }
  .wd-hero-logo {
    width: 96px; height: 96px; border-radius: 50%; object-fit: cover;
    margin-bottom: 32px;
    border: 1px solid var(--border-w2);
    box-shadow: 0 0 0 8px rgba(223,172,6,0.06), 0 0 60px var(--gold-glow);
    animation: wdLogoFloat 4s ease-in-out infinite;
  }
  @keyframes wdLogoFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }
  .wd-chip {
    display: inline-flex; align-items: center; gap: 7px;
    border: 1px solid var(--border-w2); border-radius: 999px;
    padding: 4px 14px 4px 8px; font-size: 0.72rem; font-weight: 600;
    color: var(--muted); margin-bottom: 20px; background: rgba(255,255,255,0.03);
    letter-spacing: 0.05em; text-transform: uppercase;
  }
  .wd-chip-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--gold); box-shadow: 0 0 8px var(--gold);
    animation: wdDotPulse 2s ease-in-out infinite;
  }
  @keyframes wdDotPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .wd-hero-eyebrow {
    font-family: 'Geist Mono', monospace; font-size: 0.75rem; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--dim); margin-bottom: 20px;
  }
  .wd-hero h1 {
    font-size: clamp(3rem, 8vw, 6.5rem); font-weight: 700;
    letter-spacing: -0.045em; line-height: 1.0;
    color: #fff; margin-bottom: 24px; max-width: 900px;
  }
  .wd-hero h1 .gold { color: var(--gold); text-shadow: 0 0 60px var(--gold-glow); }
  .wd-hero p {
    font-size: 1.05rem; color: var(--muted);
    max-width: 520px; margin: 0 auto 44px; line-height: 1.75;
  }
  .wd-hero-btns { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }

  /* BUTTONS */
  .wd-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: var(--r);
    font-size: 0.85rem; font-weight: 600; cursor: pointer;
    text-decoration: none; transition: all 0.2s;
    font-family: 'Plus Jakarta Sans', sans-serif; border: none; letter-spacing: 0.01em;
  }
  .wd-btn-outline {
    background: transparent; border: 1px solid var(--border-w2); color: var(--muted);
  }
  .wd-btn-outline:hover { border-color: rgba(255,255,255,0.2); color: #fff; background: rgba(255,255,255,0.04); }
  .wd-btn-gold {
    background: var(--gold); color: #0a0600;
    box-shadow: 0 0 20px var(--gold-glow);
  }
  .wd-btn-gold:hover { background: #c99c05; transform: translateY(-1px); box-shadow: 0 0 36px var(--gold-glow); }

  /* ALERT */
  .wd-alert-wrap {
    position: relative; z-index: 2;
    max-width: 800px; margin: 0 auto; padding: 0 24px 72px;
  }

  /* PREVIEW */
  .wd-preview-wrap {
    position: relative; z-index: 2; padding: 0 24px 80px;
    max-width: 1160px; margin: 0 auto;
  }
  .wd-preview-frame {
    border: 1px solid var(--border-w2); border-radius: 16px; overflow: hidden;
    box-shadow:
      0 0 0 1px rgba(223,172,6,0.08),
      0 0 80px var(--gold-glow),
      0 40px 120px rgba(0,0,0,0.7);
    transition: transform 0.4s;
  }
  .wd-preview-frame:hover { transform: scale(1.004); }
  .wd-preview-bar {
    height: 36px; background: var(--bg3); border-bottom: 1px solid var(--border-w);
    display: flex; align-items: center; padding: 0 14px; gap: 6px;
  }
  .wd-preview-dot { width: 10px; height: 10px; border-radius: 50%; }
  .wd-preview-url {
    flex: 1; height: 20px; background: var(--bg2); border-radius: 4px;
    border: 1px solid var(--border-w); margin: 0 12px;
    display: flex; align-items: center; padding: 0 10px;
    font-family: 'Geist Mono', monospace; font-size: 0.65rem; color: var(--dim);
  }
  .wd-preview-frame img { display: block; width: 100%; }

  /* SECTIONS */
  .wd-section { position: relative; z-index: 2; padding: 80px 24px; }
  .wd-section-inner { max-width: 1100px; margin: 0 auto; }
  .wd-section-tag {
    font-family: 'Geist Mono', monospace; font-size: 0.72rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase; color: var(--gold);
    margin-bottom: 10px; display: block;
  }
  .wd-section-title {
    font-size: clamp(1.7rem, 3.5vw, 2.5rem); font-weight: 700;
    letter-spacing: -0.03em; color: #fff; line-height: 1.1; margin-bottom: 14px;
  }
  .wd-section-sub { font-size: 0.95rem; color: var(--muted); max-width: 460px; line-height: 1.75; }
  .wd-section-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    gap: 32px; flex-wrap: wrap; margin-bottom: 48px;
  }

  /* STAT ROW */
  .wd-stat-row {
    display: grid; grid-template-columns: repeat(3,1fr);
    gap: 1px; background: var(--border-w);
    border: 1px solid var(--border-w); border-radius: var(--r2); overflow: hidden;
    margin-bottom: 60px;
  }
  .wd-stat-cell { background: var(--bg2); padding: 28px 24px; }
  .wd-stat-num {
    font-family: 'Geist Mono', monospace; font-size: 2.2rem;
    font-weight: 600; color: var(--gold); margin-bottom: 6px;
    text-shadow: 0 0 24px var(--gold-glow);
  }
  .wd-stat-label { font-size: 0.82rem; color: var(--muted); }

  /* AI TOOL GRID (cell style matching homepage features) */
  .wd-tool-grid {
    display: grid; grid-template-columns: repeat(3,1fr);
    gap: 1px; background: var(--border-w);
    border: 1px solid var(--border-w); border-radius: var(--r2); overflow: hidden;
  }
  .wd-tool-cell {
    background: var(--bg2); padding: 32px 26px;
    display: flex; flex-direction: column; gap: 10px;
    transition: background 0.2s;
  }
  .wd-tool-cell:hover { background: var(--bg3); }
  .wd-tool-icon { font-size: 1.8rem; }
  .wd-tool-name { font-size: 0.95rem; font-weight: 700; color: #fff; }
  .wd-tool-desc { font-size: 0.83rem; color: var(--muted); line-height: 1.65; flex: 1; }
  .wd-tool-tag {
    display: inline-flex; align-items: center; width: fit-content;
    font-family: 'Geist Mono', monospace; font-size: 0.65rem; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 8px; border-radius: 4px; margin-top: 4px;
    border: 1px solid rgba(223,172,6,0.3); color: var(--gold); background: var(--gold-dim);
  }

  /* FEATURE CARDS (with image) */
  .wd-feat-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
  .wd-feat-card {
    border: 1px solid var(--border-w); border-radius: var(--r2); overflow: hidden;
    background: var(--bg2); display: flex; flex-direction: column;
    transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
  }
  .wd-feat-card:hover {
    border-color: rgba(223,172,6,0.3); transform: translateY(-3px);
    box-shadow: 0 12px 48px rgba(223,172,6,0.1);
  }
  .wd-feat-img {
    width: 100%; height: 160px; object-fit: cover;
    border-bottom: 1px solid var(--border-w);
    filter: brightness(0.85) saturate(0.7);
    transition: filter 0.3s;
  }
  .wd-feat-card:hover .wd-feat-img { filter: brightness(1) saturate(1); }
  .wd-feat-body { padding: 22px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
  .wd-feat-icon { font-size: 1.3rem; }
  .wd-feat-name { font-size: 0.92rem; font-weight: 700; color: #fff; }
  .wd-feat-desc { font-size: 0.82rem; color: var(--muted); line-height: 1.65; }

  /* DIVIDER */
  .wd-divider {
    position: relative; z-index: 2;
    height: 1px; background: var(--border-w);
    max-width: 1100px; margin: 0 auto;
  }

  /* FOOTER */
  .wd-footer {
    position: relative; z-index: 2;
    border-top: 1px solid var(--border-w);
    padding: 28px 32px;
    display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;
  }
  .wd-footer p { font-size: 0.78rem; color: var(--dim); font-family: 'Geist Mono', monospace; }
  .wd-footer a { color: var(--gold); text-decoration: none; }
  .wd-footer a:hover { color: #fff; }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .wd-tool-grid { grid-template-columns: 1fr 1fr; }
    .wd-feat-grid { grid-template-columns: 1fr 1fr; }
    .wd-stat-row { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 580px) {
    .wd-tool-grid, .wd-feat-grid, .wd-stat-row { grid-template-columns: 1fr; }
    .wd-nav { padding: 0 16px; }
    .wd-section { padding: 56px 16px; }
    .wd-hero { padding: 90px 16px 60px; }
  }
`;

const AI_TOOLS = [
  { icon: "🤖", name: "WeTutor", desc: "A custom-trained AI tutor based on Mistral, Qwen, and LLaMA 3 — personalized explanations, exam prep, and instant clarifications.", tag: "AI · LLM" },
  { icon: "🃏", name: "Flashcard Generator", desc: "Instantly turn your notes or topics into smart flashcards with AI — ideal for rapid, focused revision.", tag: "AI · NLP" },
  { icon: "🗓️", name: "Smart Planner", desc: "Optimize your study week with AI-generated schedules tailored to your goals, habits, and deadlines.", tag: "AI · Planning" },
];

const MORE_FEATURES = [
  { icon: "🎥", name: "Study With Me", img: "/study.png", desc: "Join live or recorded focus sessions with others — stay accountable and boost productivity through shared momentum." },
  { icon: "🧪", name: "Quizzes", img: "/quiz.png", desc: "Practice anytime with AI-generated quizzes that test and reinforce what you've learned, adapting to your weak spots." },
  { icon: "🗳️", name: "Vote", img: "/vote.png", desc: "Participate in daily votes inside the private study space — shape room themes, goals, and upcoming challenges." },
];

const STATS = [
  { num: "3", label: "AI models powering WeTutor" },
  { num: "100%", label: "GDPR compliant data handling" },
  { num: "Beta", label: "Actively developed & improving" },
];

const WeStudyDigital = () => (
  <>
    <style>{css}</style>
    <div className="wd-root">

      <div className="wd-grid-bg" />



      {/* NAV */}
      <nav className="wd-nav">
        <a href="/" className="wd-nav-logo">
          <img src="/logogo.png" alt="WeStudy" />
          WeStudy
        </a>
        <div className="wd-nav-right">
          <span className="wd-nav-badge">Digital · Beta</span>
          <a href="/" className="wd-nav-back">← Home</a>
        </div>
      </nav>

      {/* HERO */}
      <div className="wd-hero">
        <img src="/logobr.png" alt="WeStudy Digital" className="wd-hero-logo" />
        <span className="wd-chip"><span className="wd-chip-dot" />Beta · Under Active Development</span>
        <p className="wd-hero-eyebrow">Powered by AI · Built for Students</p>
        <h1>WeStudy <span className="gold">Digital</span></h1>
        <p>Explore how WeStudy blends technology and education through AI, connected environments, and intelligent tools built around the way you actually study.</p>
        <div className="wd-hero-btns">
          <a href="#ai" className="wd-btn wd-btn-gold">Explore AI Tools</a>
          <a href="#features" className="wd-btn wd-btn-outline">More Features</a>
        </div>
      </div>

      {/* BETA ALERT */}
      <div className="wd-alert-wrap">
        <Alert
          message="⚠️ Beta Features"
          description="All features are currently in beta and under intensive development. Personal data is strictly secured and handled in full compliance with GDPR protocols."
          type="warning"
          showIcon
          style={{
            borderRadius: 10,
            background: 'rgba(223,172,6,0.07)',
            border: '1px solid rgba(223,172,6,0.25)',
          }}
        />
      </div>

      {/* DASHBOARD PREVIEW */}
      <div className="wd-preview-wrap">
        <div className="wd-preview-frame">
          <div className="wd-preview-bar">
            <div className="wd-preview-dot" style={{ background: '#ff5f57' }} />
            <div className="wd-preview-dot" style={{ background: '#febc2e' }} />
            <div className="wd-preview-dot" style={{ background: '#28c840' }} />
            <div className="wd-preview-url">app.westudy.tn · Dashboard</div>
          </div>
          <img src="/dashboard_preview.png" alt="WeStudy App Preview" />
        </div>
      </div>

      {/* AI TOOLS */}
      <div className="wd-section" id="ai">
        <div className="wd-section-inner">
          <div className="wd-stat-row">
            {STATS.map((s, i) => (
              <div key={i} className="wd-stat-cell">
                <div className="wd-stat-num">{s.num}</div>
                <div className="wd-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="wd-section-header">
            <div>
              <span className="wd-section-tag">Artificial Intelligence</span>
              <h2 className="wd-section-title">Our AI Tools</h2>
            </div>
            <p className="wd-section-sub">Powered by open-source LLMs, our AI suite is built specifically around how students actually learn.</p>
          </div>
          <div className="wd-tool-grid">
            {AI_TOOLS.map((t, i) => (
              <div key={i} className="wd-tool-cell">
                <div className="wd-tool-icon">{t.icon}</div>
                <div className="wd-tool-name">{t.name}</div>
                <div className="wd-tool-desc">{t.desc}</div>
                <span className="wd-tool-tag">{t.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wd-divider" />

      {/* MORE FEATURES */}
      <div className="wd-section" id="features">
        <div className="wd-section-inner">
          <div className="wd-section-header">
            <div>
              <span className="wd-section-tag">Platform Features</span>
              <h2 className="wd-section-title">More Digital Features</h2>
            </div>
            <p className="wd-section-sub">Beyond AI — tools to keep you engaged, accountable, and productive inside the WeStudy ecosystem.</p>
          </div>
          <div className="wd-feat-grid">
            {MORE_FEATURES.map((f, i) => (
              <div key={i} className="wd-feat-card">
                <img src={f.img} alt={f.name} className="wd-feat-img" />
                <div className="wd-feat-body">
                  <div className="wd-feat-icon">{f.icon}</div>
                  <div className="wd-feat-name">{f.name}</div>
                  <div className="wd-feat-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="wd-footer">
        <p>© {new Date().getFullYear()} WeStudy · Digital Platform</p>
        <p><a href="/">← Back to main site</a></p>
      </footer>

    </div>
  </>
);

export default WeStudyDigital;